import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

// localStorage key per user
const storageKey = (userId) => `emh_carts_${userId}`;

// Read carts + activeCartKey from localStorage for a given user
function loadFromStorage(userId) {
  if (!userId) return { carts: {}, activeCartKey: null };
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        carts: parsed.carts || {},
        activeCartKey: parsed.activeCartKey || null,
      };
    }
  } catch (e) {
    console.error('CartContext: failed to load from localStorage', e);
  }
  return { carts: {}, activeCartKey: null };
}

// Write carts + activeCartKey to localStorage for a given user
function saveToStorage(userId, carts, activeCartKey) {
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify({ carts, activeCartKey }));
  } catch (e) {
    console.error('CartContext: failed to save to localStorage', e);
  }
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || null;

  const [carts, setCarts] = useState({});           // { cartKey: { customerId, customerName, items: [] } }
  const [activeCartKey, setActiveCartKey] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const toastTimer = useRef(null);
  const initialized = useRef(false);
  const currentUserId = useRef(null);

  // Load carts from localStorage when user changes
  useEffect(() => {
    if (userId && userId !== currentUserId.current) {
      const stored = loadFromStorage(userId);
      setCarts(stored.carts);
      // Validate that activeCartKey still exists in carts
      const validKey = stored.activeCartKey && stored.carts[stored.activeCartKey]
        ? stored.activeCartKey
        : null;
      setActiveCartKey(validKey);
      currentUserId.current = userId;
      initialized.current = true;
    } else if (!userId) {
      // No user — reset
      setCarts({});
      setActiveCartKey(null);
      currentUserId.current = null;
      initialized.current = false;
    }
  }, [userId]);

  // Persist to localStorage on every carts/activeCartKey change (skip initial default state)
  useEffect(() => {
    if (!initialized.current || !userId) return;
    saveToStorage(userId, carts, activeCartKey);
  }, [carts, activeCartKey, userId]);

  // Auto-dismiss toast cleanup
  useEffect(() => {
    return () => { if (toastTimer.current) clearTimeout(toastTimer.current); };
  }, []);

  const triggerToast = useCallback((msg) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMessage(msg);
    setShowToast(true);
    toastTimer.current = setTimeout(() => setShowToast(false), 2000);
  }, []);

  // --- Cart management ---

  const createCart = useCallback((customerId, customerName) => {
    const key = `${userId}_${customerId || 'walkin'}`;

    // If cart already exists for this customer, just switch to it
    let alreadyExists = false;
    setCarts(prev => {
      if (prev[key]) {
        alreadyExists = true;
        return prev; // no change
      }
      return {
        ...prev,
        [key]: {
          customerId: customerId || 'walkin',
          customerName: customerName || 'Walk-in',
          items: [],
          createdAt: Date.now(),
        },
      };
    });

    setActiveCartKey(key);
    return key;
  }, [userId]);

  const switchCart = useCallback((cartKey) => {
    setActiveCartKey(cartKey);
  }, []);

  const deleteCart = useCallback((cartKey) => {
    setCarts(prev => {
      const next = { ...prev };
      delete next[cartKey];
      return next;
    });
    setActiveCartKey(prev => prev === cartKey ? null : prev);
  }, []);

  // --- Item operations (all operate on active cart) ---

  const addToCart = useCallback((product) => {
    if (!activeCartKey) return;
    setCarts(prev => {
      const cart = prev[activeCartKey];
      if (!cart) return prev;

      const items = cart.items;
      const existing = items.find(i => i.sku === product.sku || i.sku === product.id);
      let newItems;

      if (existing) {
        if (existing.qty >= (product.stock || Infinity)) return prev;
        newItems = items.map(i =>
          (i.sku === product.sku || i.sku === product.id) ? { ...i, qty: i.qty + 1 } : i
        );
      } else {
        newItems = [...items, {
          sku: product.sku || product.id,
          name: product.name || '',
          price: product.price || 0,
          cost: product.cost || 0,
          qty: 1,
          location: product.location || '',
          brand: product.brand || '',
          stock: product.stock || 0,
        }];
      }

      return {
        ...prev,
        [activeCartKey]: { ...cart, items: newItems },
      };
    });
  }, [activeCartKey]);

  const removeFromCart = useCallback((sku) => {
    if (!activeCartKey) return;
    setCarts(prev => {
      const cart = prev[activeCartKey];
      if (!cart) return prev;
      return {
        ...prev,
        [activeCartKey]: { ...cart, items: cart.items.filter(i => i.sku !== sku) },
      };
    });
  }, [activeCartKey]);

  const updateQty = useCallback((sku, qty) => {
    if (!activeCartKey) return;
    setCarts(prev => {
      const cart = prev[activeCartKey];
      if (!cart) return prev;
      let newItems;
      if (qty <= 0) {
        newItems = cart.items.filter(i => i.sku !== sku);
      } else {
        newItems = cart.items.map(i => i.sku === sku ? { ...i, qty } : i);
      }
      return {
        ...prev,
        [activeCartKey]: { ...cart, items: newItems },
      };
    });
  }, [activeCartKey]);

  const clearActiveCart = useCallback(() => {
    if (!activeCartKey) return;
    setCarts(prev => {
      const cart = prev[activeCartKey];
      if (!cart) return prev;
      return {
        ...prev,
        [activeCartKey]: { ...cart, items: [] },
      };
    });
  }, [activeCartKey]);

  // --- Computed values ---

  const activeCart = carts[activeCartKey] || null;
  const activeCartItems = activeCart ? activeCart.items : [];

  const cartCount = useMemo(() => {
    return Object.values(carts).reduce(
      (sum, cart) => sum + cart.items.reduce((s, i) => s + i.qty, 0),
      0
    );
  }, [carts]);

  const activeCartCount = useMemo(() => {
    return activeCartItems.reduce((sum, i) => sum + i.qty, 0);
  }, [activeCartItems]);

  const cartList = useMemo(() => {
    return Object.entries(carts).map(([key, cart]) => ({
      key,
      ...cart,
      itemCount: cart.items.reduce((s, i) => s + i.qty, 0),
    }));
  }, [carts]);

  // --- Context value ---

  const value = useMemo(() => ({
    // Multi-cart API
    carts,
    cartList,
    activeCart,
    activeCartKey,
    createCart,
    switchCart,
    deleteCart,
    addToCart,
    removeFromCart,
    updateQty,
    clearActiveCart,
    cartCount,
    activeCartCount,
    activeCartItems,

    // Legacy compat aliases
    cartItems: activeCartItems,
    clearCart: clearActiveCart,

    // Toast
    toastMessage,
    showToast,
    triggerToast,
  }), [
    carts, cartList, activeCart, activeCartKey,
    createCart, switchCart, deleteCart,
    addToCart, removeFromCart, updateQty, clearActiveCart,
    cartCount, activeCartCount, activeCartItems,
    toastMessage, showToast, triggerToast,
  ]);

  return (
    <CartContext.Provider value={value}>
      {children}
      {/* Global toast overlay */}
      {showToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[999] animate-fadeIn">
          <div className="bg-green-500/90 text-white px-5 py-2.5 rounded-full text-sm font-medium shadow-lg backdrop-blur-sm flex items-center gap-2">
            <span>✓</span>
            <span>{toastMessage}</span>
          </div>
        </div>
      )}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
